import React, { useEffect, useState } from "react";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";

const STAGE_COLORS = {
  Prospecting: { bg: "#EFF6FF", text: "#1D4ED8" },
  Qualification: { bg: "#EEF2FF", text: "#4338CA" },
  Proposal: { bg: "#F5F3FF", text: "#7C3AED" },
  Negotiation: { bg: "#FDF4FF", text: "#9333EA" },
  "Closed Won": { bg: "#ECFDF5", text: "#059669" },
  "Closed Lost": { bg: "#FEF2F2", text: "#DC2626" },
};

const COLS = ["Company", "Stage", "Value", "Probability", "Close Date", "Rep", "Product", "Actions"];

export default function PipelineTable({ stageFilter, rows, onUpdateStage, onExport }) {
  const [sortCol, setSortCol] = useState("value");
  const [sortDir, setSortDir] = useState("desc");
  const [editingId, setEditingId] = useState(null);
  const [editStage, setEditStage] = useState("");
  const [tableDeals, setTableDeals] = useState([]);
  const [page, setPage] = useState(0);
  const perPage = 5;

  useEffect(() => {
    const mappedRows = (rows || []).map((row, index) => ({
      id: row.opportunityId || row.id || index + 1,
      company: row.company,
      contact: row.contact,
      stage: row.stage,
      value: row.value,
      probability: row.probabilityPercent,
      closeDate: row.closeDateUtc ? new Date(row.closeDateUtc).toLocaleDateString("en-IN") : "—",
      rep: row.rep,
      product: row.product,
    }));

    setTableDeals(mappedRows);
    setPage(0);
  }, [rows]);

  const filtered = stageFilter
    ? tableDeals?.filter((d) => d?.stage?.toLowerCase()?.replace(" ", "_") === stageFilter)
    : tableDeals;

  const sorted = [...filtered]?.sort((a, b) => {
    let av = a?.[sortCol], bv = b?.[sortCol];
    if (typeof av === "string") av = av?.toLowerCase();
    if (typeof bv === "string") bv = bv?.toLowerCase();
    return sortDir === "asc" ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1);
  });

  const paginated = sorted?.slice(page * perPage, (page + 1) * perPage);
  const totalPages = Math.ceil(sorted?.length / perPage);

  const handleSort = (col) => {
    const key = col?.toLowerCase()?.replace(" ", "");
    const colMap = { company: "company", stage: "stage", value: "value", probability: "probability", closedate: "closeDate", rep: "rep", product: "product" };
    const mapped = colMap?.[key];
    if (!mapped) return;
    if (sortCol === mapped) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortCol(mapped); setSortDir("desc"); }
  };

  const saveEdit = async (id) => {
    const previousDeals = tableDeals;
    setTableDeals(prev => prev?.map(d => d?.id === id ? { ...d, stage: editStage } : d));
    setEditingId(null);

    if (!onUpdateStage) {
      return;
    }

    try {
      await onUpdateStage(id, editStage);
    } catch {
      setTableDeals(previousDeals);
    }
  };

  return (
    <div
      className="card-base p-4 md:p-6"
      style={{ background: "var(--color-card)", border: "1px solid var(--color-border)" }}
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-heading font-semibold text-base md:text-lg" style={{ color: "var(--color-foreground)" }}>
            Active Pipeline
          </h3>
          <p className="text-xs font-caption mt-0.5" style={{ color: "var(--color-text-secondary)" }}>
            {filtered?.length} deals · Click stage to inline edit
          </p>
        </div>
        <Button variant="outline" size="sm" iconName="Download" iconPosition="left" iconSize={14} onClick={onExport}>
          Export
        </Button>
      </div>
      <div className="table-container">
        <table className="w-full text-sm">
          <thead>
            <tr style={{ borderBottom: "1px solid var(--color-border)" }}>
              {COLS?.map((col) => (
                <th
                  key={col}
                  className="text-left pb-3 pr-4 font-caption font-medium text-xs cursor-pointer select-none whitespace-nowrap"
                  style={{ color: "var(--color-text-secondary)" }}
                  onClick={() => handleSort(col)}
                >
                  <span className="flex items-center gap-1">
                    {col}
                    {col !== "Actions" && (
                      <Icon
                        name={sortCol === col?.toLowerCase()?.replace(" ", "") ? (sortDir === "asc" ? "ChevronUp" : "ChevronDown") : "ChevronsUpDown"}
                        size={12}
                        color="currentColor"
                      />
                    )}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginated?.map((deal) => {
              const sc = STAGE_COLORS?.[deal?.stage] || { bg: "#F1F5F9", text: "#475569" };
              return (
                <tr
                  key={deal?.id}
                  className="border-b hover:bg-slate-50 transition-colors duration-150"
                  style={{ borderColor: "var(--color-border)" }}
                >
                  <td className="py-3 pr-4">
                    <div>
                      <p className="font-medium font-caption text-sm" style={{ color: "var(--color-foreground)" }}>{deal?.company}</p>
                      <p className="text-xs font-caption" style={{ color: "var(--color-text-secondary)" }}>{deal?.contact}</p>
                    </div>
                  </td>
                  <td className="py-3 pr-4">
                    {editingId === deal?.id ? (
                      <select
                        value={editStage}
                        onChange={(e) => setEditStage(e?.target?.value)}
                        className="text-xs border rounded px-2 py-1 font-caption"
                        style={{ borderColor: "var(--color-border)", background: "var(--color-card)", color: "var(--color-foreground)" }}
                      >
                        {Object.keys(STAGE_COLORS)?.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    ) : (
                      <span
                        className="px-2 py-1 rounded-full text-xs font-medium font-caption whitespace-nowrap"
                        style={{ background: sc?.bg, color: sc?.text }}
                      >
                        {deal?.stage}
                      </span>
                    )}
                  </td>
                  <td className="py-3 pr-4 font-data text-sm font-medium whitespace-nowrap" style={{ color: "var(--color-foreground)" }}>
                    ₹{(deal?.value / 100000)?.toFixed(1)}L
                  </td>
                  <td className="py-3 pr-4">
                    <div className="flex items-center gap-2">
                      <div className="w-16 h-1.5 rounded-full overflow-hidden" style={{ background: "var(--color-muted)" }}>
                        <div
                          className="h-full rounded-full"
                          style={{
                            width: `${deal?.probability}%`,
                            background: deal?.probability >= 70 ? "var(--color-success)" : deal?.probability >= 40 ? "var(--color-warning)" : "var(--color-error)",
                          }}
                        />
                      </div>
                      <span className="text-xs font-data whitespace-nowrap" style={{ color: "var(--color-text-secondary)" }}>
                        {deal?.probability}%
                      </span>
                    </div>
                  </td>
                  <td className="py-3 pr-4 text-xs font-data whitespace-nowrap" style={{ color: "var(--color-foreground)" }}>
                    {deal?.closeDate}
                  </td>
                  <td className="py-3 pr-4 text-xs font-caption whitespace-nowrap" style={{ color: "var(--color-foreground)" }}>
                    {deal?.rep}
                  </td>
                  <td className="py-3 pr-4 text-xs font-caption whitespace-nowrap" style={{ color: "var(--color-text-secondary)" }}>
                    {deal?.product}
                  </td>
                  <td className="py-3">
                    {editingId === deal?.id ? (
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => saveEdit(deal?.id)}
                          className="p-1 rounded hover:bg-emerald-50 transition-colors"
                          aria-label="Save edit"
                        >
                          <Icon name="Check" size={14} color="var(--color-success)" />
                        </button>
                        <button
                          onClick={() => setEditingId(null)}
                          className="p-1 rounded hover:bg-red-50 transition-colors"
                          aria-label="Cancel edit"
                        >
                          <Icon name="X" size={14} color="var(--color-error)" />
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => { setEditingId(deal?.id); setEditStage(deal?.stage); }}
                        className="p-1 rounded hover:bg-slate-100 transition-colors"
                        aria-label="Edit deal stage"
                      >
                        <Icon name="Pencil" size={14} color="var(--color-text-secondary)" />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 pt-3 border-t" style={{ borderColor: "var(--color-border)" }}>
          <span className="text-xs font-caption" style={{ color: "var(--color-text-secondary)" }}>
            Showing {page * perPage + 1}–{Math.min((page + 1) * perPage, sorted?.length)} of {sorted?.length}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
              className="flex items-center justify-center w-8 h-8 rounded-md border transition-colors disabled:opacity-40"
              style={{ borderColor: "var(--color-border)", background: "var(--color-card)" }}
              aria-label="Previous page"
            >
              <Icon name="ChevronLeft" size={14} color="var(--color-foreground)" />
            </button>
            <span className="text-xs font-data" style={{ color: "var(--color-foreground)" }}>
              {page + 1} / {totalPages}
            </span>
            <button
              onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
              disabled={page === totalPages - 1}
              className="flex items-center justify-center w-8 h-8 rounded-md border transition-colors disabled:opacity-40"
              style={{ borderColor: "var(--color-border)", background: "var(--color-card)" }}
              aria-label="Next page"
            >
              <Icon name="ChevronRight" size={14} color="var(--color-foreground)" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}